<script setup lang="ts">
import { provide, ref, StyleValue, type LabelHTMLAttributes } from 'vue';
import { labelInjectionKey } from './internal';

type LabelProps = {
  /** The text content of the label. */
  text: string;
  /** The unique identifier of the element associated with this label.
   *
   * **Do not provide this property if you are using this label as a wrapper!** The first slotted input element associates itself with the label if this property is omitted.
   *
   * If you want to overwrite this mechanism (there are cases where this actually makes sense), you are free to do so, but you have been warned. */
  for?: string;
  /** The text alignment of the label. Defaults to `'start'` */
  textAlign?: 'start' | 'end';
  /** The color of the label. Defaults to `'var(--nxt-brand-bg)'` */
  color?: string;
  /** Additional styling for the wrapper element if necessary. The wrapper element is only included if there are slotted elements. Defaults to `undefined` */
  wrapperStyle?: StyleValue;
  /** Fill a bounded column; the caption keeps its natural height. */
  fill?: boolean;
};

const props = defineProps<LabelProps & /* @vue-ignore */ LabelHTMLAttributes>();

// Assign the shorthand style properties to the style attribute of the label (the default values are declared by the stylesheet)
const labelStyle: StyleValue = { textAlign: props.textAlign, color: props.color };

// Assign the for property to a reactive object. The reactive object is assigned to the for attribute of the label
const labelFor = ref<string | undefined>(props.for);
// The label injection is used to associate the first slotted input element with the label if the label isn't associated with another element yet
const setLabelFor = (elementId: string) => {
  // The function should only be called after verifying no element has been associated yet but better be safe than sorry
  labelFor.value ??= elementId;
};
provide(labelInjectionKey, { for: labelFor, setFor: setLabelFor });

// Disable the default fallthrough attributes behavior. The fallthrough attributes always have to be assigned to the label even if the label wrapper is the root element
defineOptions({ inheritAttrs: false });
</script>
<template>
  <div
    v-if="$slots.default"
    :style="$props.wrapperStyle"
    class="label-wrapper"
    :class="{ 'is-fill': props.fill }"
  >
    <span v-if="$slots.info" class="label-row">
      <label v-bind="$attrs" :style="labelStyle" :for="labelFor" v-text="$props.text" />
      <!-- Inline adornment BESIDE the label text (e.g. an info-tooltip trigger).
           Rendered outside the <label> on purpose: a control nested inside a
           label forwards its clicks to the associated input. -->
      <slot name="info" />
    </span>
    <label v-else v-bind="$attrs" :style="labelStyle" :for="labelFor" v-text="$props.text" />
    <slot />
  </div>
  <span v-else-if="$slots.info" class="label-row">
    <label v-bind="$attrs" :style="labelStyle" :for="labelFor" v-text="$props.text" />
    <slot name="info" />
  </span>
  <label v-else v-bind="$attrs" :style="labelStyle" :for="labelFor" v-text="$props.text" />
</template>
<style scoped>
.label-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  width: 100%;
}

.label-row {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
}

.label-wrapper.is-fill {
  flex: 1 1 auto;
  min-height: 0;

  & > label,
  & > .label-row {
    flex: none;
  }
}

/* A small heading: the design's caps axis (data-nx-caps, I-2510) sets it in
   small capitals; by default it stays as written. */
label {
  color: var(--nxt-label-fg);
  font-size: var(--nxt-label-size);
  font-weight: var(--nxt-font-weight-regular);
  letter-spacing: var(--nxd-caption-tracking, normal);
  text-align: start;
  text-transform: var(--nxd-caption-transform, none);
}
</style>
